/**
 * Line Prompt Visualizer
 *
 * Provides visual feedback utilities for line selection and interaction.
 * This module handles:
 * - Preview line rendering (dashed lines with spheres)
 * - Prompt sphere and direction line visualization
 * - Isolation plane rendering for line hover states
 * - Box preview for extrusion operations
 *
 * These utilities can be reused across different tools that need
 * line selection visual feedback.
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LinePrompt, DetectedLine } from './types';
import { Viewer } from '../../../../viewer/viewer';
import { PointoramaPlane } from '../../../../utils/PointoramaPlane';
import { ClipTask } from '../../../../defines';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';

/** Snap target sphere color - cyan to distinguish from prompt spheres */
const SNAP_TARGET_COLOR = 0x00bcd4;

/** Brand green (Neon Green) - used for prompt handle spheres */
const HANDLE_COLOR = 0x7edd64;

/** Brand purple (French Violet) - used for lines and highlights */
const LINE_COLOR = 0x854dd6;

/** Orange color for selected-for-deletion prompt spheres (distinct from unconnected red) */
const DELETION_HIGHLIGHT_COLOR = 0xff4444;

/**
 * Visual feedback manager for line selection
 */
export class LinePromptVisualizer {
  private linePreviewVisual: THREE.Group | null = null;
  private boxPreviewVisual: THREE.Group | null = null;
  private snappedBoxPreviewVisual: THREE.Group | null = null;
  private promptVisuals: THREE.Group | null = null;
  private promptSpheres: Map<string, THREE.Mesh> = new Map();
  private isolationPlane: PointoramaPlane | null = null;
  private snapTargetVisuals: THREE.Group | null = null;

  constructor(
    private viewer: Viewer,
    private coordinateTransformer: CoordinateTransformer,
    private scene: THREE.Scene,
  ) {}

  /**
   * Scene used for the line prompts (handles + their direction lines).
   * The viewer's UI scene is rendered last with a depth clear, so prompt
   * handles always draw on top of the shape fill/outline and other overlays.
   */
  private get promptScene(): THREE.Scene {
    return this.viewer.uiScene ?? this.scene;
  }

  /**
   * Calculate dynamic sphere radius based on camera mode.
   * In 2D (orthographic), returns a world-space size that appears as a fixed pixel size.
   * In 3D (perspective), returns a fixed world size.
   */
  calculateDynamicSphereRadius(): number {
    const camera = this.viewer.scene.getActiveCamera();
    const is2DMode = camera.type === 'OrthographicCamera';

    if (is2DMode) {
      // For orthographic camera, calculate world-space size for desired pixel size
      const orthoCamera = camera as THREE.OrthographicCamera;
      const viewHeight = orthoCamera.top - orthoCamera.bottom;
      const screenHeight = this.viewer.renderArea.clientHeight;
      const pixelsPerUnit = screenHeight / viewHeight;
      const desiredPixelRadius = 9; // Target size in pixels
      return desiredPixelRadius / pixelsPerUnit;
    } else {
      return 0.025; // Fixed size in meters for 3D mode
    }
  }

  /**
   * Render a preview line from start point to end point with dashed style.
   * Used for two-click line creation and perpendicular snap preview.
   */
  renderLinePreview(startWorldPoint: THREE.Vector3, endWorldPoint: THREE.Vector3): void {
    // Clear existing preview
    this.clearLinePreview();

    // Create preview visualization
    this.linePreviewVisual = new THREE.Group();

    // Use start world point as local origin
    const localOrigin = startWorldPoint.clone();
    this.linePreviewVisual.position.copy(localOrigin);

    const localStart = new THREE.Vector3(0, 0, 0); // Start point at origin
    const localEnd = endWorldPoint.clone().sub(localOrigin);

    // Create preview line
    const positions = [localStart.x, localStart.y, localStart.z, localEnd.x, localEnd.y, localEnd.z];

    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions(positions);

    const lineMaterial = new LineMaterial({
      color: LINE_COLOR,
      linewidth: 3,
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      depthTest: false,
      transparent: true,
      opacity: 0.7,
      dashed: true,
      dashScale: 10,
      dashSize: 1,
      gapSize: 0.5,
    });

    const previewLine = new Line2(lineGeometry, lineMaterial);
    previewLine.computeLineDistances();
    previewLine.renderOrder = 1000;
    this.linePreviewVisual.add(previewLine);

    // Calculate dynamic sphere radius (same as prompt spheres)
    const sphereRadius = this.calculateDynamicSphereRadius();

    // Add sphere at start point
    const sphereGeometry = new THREE.SphereGeometry(sphereRadius, 12, 12);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: HANDLE_COLOR,
      depthTest: false,
      transparent: true,
      opacity: 0.9,
    });
    const startSphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    startSphere.position.copy(localStart);
    startSphere.renderOrder = 1001;
    this.linePreviewVisual.add(startSphere);

    // Add sphere at end position
    const endSphere = new THREE.Mesh(sphereGeometry.clone(), sphereMaterial.clone());
    endSphere.position.copy(localEnd);
    endSphere.renderOrder = 1001;
    this.linePreviewVisual.add(endSphere);

    this.scene.add(this.linePreviewVisual);
  }

  /**
   * Clear the line preview visualization
   */
  clearLinePreview(): void {
    if (this.linePreviewVisual) {
      this.scene.remove(this.linePreviewVisual);
      this.linePreviewVisual.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof Line2) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.linePreviewVisual = null;
    }
  }

  /**
   * Update the 3D visualization of all prompts with direction lines.
   * Uses local coordinate system to avoid floating point precision issues.
   * Lines are adjusted to show only connected segments when connections are found.
   * @param prompts - The prompts to visualize
   * @param hoveredPromptId - ID of the currently hovered prompt (rendered larger)
   * @param highlightedPromptIds - Set of prompt IDs to highlight in red (e.g., during deletion preview)
   * @param shiftHighlightedPromptId - ID of prompt highlighted as perpendicular snap target (shift-hover)
   */
  updatePromptVisuals(prompts: LinePrompt[], hoveredPromptId: string | null = null, highlightedPromptIds: Set<string> | null = null, shiftHighlightedPromptId: string | null = null): void {
    // Clear existing visuals
    if (this.promptVisuals) {
      this.promptScene.remove(this.promptVisuals);
      this.promptVisuals.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof Line2) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
    }

    // Clear sphere tracking
    this.promptSpheres.clear();

    if (prompts.length === 0) {
      this.promptVisuals = null;
      return;
    }

    this.promptVisuals = new THREE.Group();

    // Use the first prompt's position as the local origin to avoid precision issues
    const localOrigin = prompts[0].worldPoint.clone();
    this.promptVisuals.position.copy(localOrigin);

    const directionLineLength = 3.0; // Length of direction indicator line in meters (when no connections)

    // Calculate sphere radius using shared helper
    const sphereRadius = this.calculateDynamicSphereRadius();

    for (const prompt of prompts) {
      // Calculate position relative to the group's local origin
      const localPosition = prompt.worldPoint.clone().sub(localOrigin);

      // Check if prompt is highlighted for deletion
      const isHighlightedForDeletion = highlightedPromptIds?.has(prompt.id) ?? false;

      // Check if prompt has both connections (fully connected)
      const isFullyConnected = prompt.connections?.positive !== undefined && prompt.connections?.negative !== undefined;

      // Check if this prompt is the shift-highlighted snap target
      const isShiftHighlighted = prompt.id === shiftHighlightedPromptId;

      // Determine sphere color: deletion highlight > shift highlight > connection status
      let sphereColor: number;
      if (isHighlightedForDeletion) {
        sphereColor = DELETION_HIGHLIGHT_COLOR;
      } else if (isShiftHighlighted) {
        sphereColor = SNAP_TARGET_COLOR;
      } else if (isFullyConnected) {
        sphereColor = HANDLE_COLOR;
      } else {
        sphereColor = 0xf44336; // Red if not fully connected
      }

      // Create direction lines first so the handle sphere (added last) draws
      // on top of them — sortObjects is disabled, so scene-child order wins.
      const hasPositiveConnection = prompt.connections?.positive !== undefined;
      const hasNegativeConnection = prompt.connections?.negative !== undefined;

      // Use stored lengths or fall back to default
      const defaultHalfLength = directionLineLength / 2;
      const posLength = prompt.positiveLength ?? defaultHalfLength;
      const negLength = prompt.negativeLength ?? defaultHalfLength;

      // Positive direction (along prompt.direction)
      if (hasPositiveConnection && prompt.connections?.positive) {
        // Connected - show line to connection point in normal color
        const endPoint = prompt.connections.positive.point.clone().sub(localOrigin);
        this.addDirectionLine(localPosition, endPoint, false);
      } else {
        // Not connected - show original length line in red
        const endPoint = localPosition.clone().add(prompt.direction.clone().multiplyScalar(posLength));
        this.addDirectionLine(localPosition, endPoint, false, true); // true = red/unconnected
      }

      // Negative direction (opposite to prompt.direction)
      if (hasNegativeConnection && prompt.connections?.negative) {
        // Connected - show line to connection point in normal color
        const endPoint = prompt.connections.negative.point.clone().sub(localOrigin);
        this.addDirectionLine(localPosition, endPoint, false);
      } else {
        // Not connected - show original length line in red
        const endPoint = localPosition.clone().add(prompt.direction.clone().multiplyScalar(-negLength));
        this.addDirectionLine(localPosition, endPoint, false, true); // true = red/unconnected
      }

      // Create the handle sphere last so it renders on top of the direction lines
      const scale = isShiftHighlighted ? 1.3 : 1.0;
      const sphereGeometry = new THREE.SphereGeometry(sphereRadius * scale, 16, 16);
      const sphereMaterial = new THREE.MeshBasicMaterial({
        color: sphereColor,
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      });
      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      sphere.position.copy(localPosition);
      sphere.renderOrder = 1001;
      sphere.userData.promptId = prompt.id; // Store prompt ID for hit testing
      this.promptVisuals.add(sphere);

      // Track sphere for hit testing
      this.promptSpheres.set(prompt.id, sphere);
    }

    this.promptScene.add(this.promptVisuals);
  }

  /**
   * Add a direction line segment to the prompt visuals
   */
  private addDirectionLine(startPoint: THREE.Vector3, endPoint: THREE.Vector3, isHovered = false, isUnconnected = false): void {
    if (!this.promptVisuals) return;

    const positions = [startPoint.x, startPoint.y, startPoint.z, endPoint.x, endPoint.y, endPoint.z];

    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions(positions);

    // Use red for unconnected sides, brand green for connected
    const lineColor = isUnconnected ? 0xf44336 : LINE_COLOR;

    const lineMaterial = new LineMaterial({
      color: lineColor,
      linewidth: isHovered ? 4 : 3, // Slightly thicker when hovered
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      depthTest: false,
      transparent: true,
      opacity: isHovered ? 1.0 : 0.9,
    });

    const directionLine = new Line2(lineGeometry, lineMaterial);
    directionLine.renderOrder = 1000;
    this.promptVisuals.add(directionLine);

    // Add subtle background line for better visibility
    const backgroundLineMaterial = new LineMaterial({
      color: 0x000000, // Black background
      linewidth: isHovered ? 6 : 5, // Slightly thicker for halo effect
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      transparent: true,
      depthTest: false,
      opacity: 0.3,
    });

    const backgroundLine = new Line2(lineGeometry.clone(), backgroundLineMaterial);
    backgroundLine.renderOrder = 999;
    this.promptVisuals.add(backgroundLine);
  }

  /**
   * Clear all prompt visuals
   */
  clearPromptVisuals(): void {
    if (this.promptVisuals) {
      this.promptScene.remove(this.promptVisuals);
      this.promptVisuals.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof Line2) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.promptVisuals = null;
    }
    this.promptSpheres.clear();
  }

  /**
   * Render a box preview when dragging from a prompt line.
   * The box consists of 4 lines forming a rectangle.
   */
  renderBoxPreview(corners: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3]): void {
    // Clear existing box preview
    this.clearBoxPreview();

    const [corner1, corner2, corner3, corner4] = corners;

    // Create the box preview group
    this.boxPreviewVisual = new THREE.Group();

    // Use corner1 as local origin
    const localOrigin = corner1.clone();
    this.boxPreviewVisual.position.copy(localOrigin);

    const localCorner1 = new THREE.Vector3(0, 0, 0);
    const localCorner2 = corner2.clone().sub(localOrigin);
    const localCorner3 = corner3.clone().sub(localOrigin);
    const localCorner4 = corner4.clone().sub(localOrigin);

    // Create 4 edges of the box
    const edges = [
      [localCorner1, localCorner2], // Edge 1-2
      [localCorner2, localCorner4], // Edge 2-4
      [localCorner4, localCorner3], // Edge 4-3
      [localCorner3, localCorner1], // Edge 3-1
    ];

    for (const [start, end] of edges) {
      const positions = [start.x, start.y, start.z, end.x, end.y, end.z];

      const lineGeometry = new LineGeometry();
      lineGeometry.setPositions(positions);

      const lineMaterial = new LineMaterial({
        color: LINE_COLOR,
        linewidth: 3,
        resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
        depthTest: false,
        transparent: true,
        opacity: 0.7,
        dashed: true,
        dashScale: 10,
        dashSize: 1,
        gapSize: 0.5,
      });

      const line = new Line2(lineGeometry, lineMaterial);
      line.computeLineDistances();
      line.renderOrder = 1000;
      this.boxPreviewVisual.add(line);
    }

    this.scene.add(this.boxPreviewVisual);
  }

  /**
   * Clear the box preview visualization
   */
  clearBoxPreview(): void {
    if (this.boxPreviewVisual) {
      this.scene.remove(this.boxPreviewVisual);
      this.boxPreviewVisual.traverse((obj) => {
        if (obj instanceof Line2) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.boxPreviewVisual = null;
    }
    this.clearSnappedBoxPreview();
  }

  /**
   * Render green solid lines showing density-snapped positions for a box extrusion.
   * Displayed alongside the dashed geometric box preview during drag.
   */
  renderSnappedBoxPreview(edges: Array<[THREE.Vector3, THREE.Vector3]>): void {
    this.clearSnappedBoxPreview();

    if (edges.length === 0) return;

    this.snappedBoxPreviewVisual = new THREE.Group();

    // Use first edge start as local origin to avoid precision issues
    const localOrigin = edges[0][0].clone();
    this.snappedBoxPreviewVisual.position.copy(localOrigin);

    for (const [start, end] of edges) {
      const localStart = start.clone().sub(localOrigin);
      const localEnd = end.clone().sub(localOrigin);
      const positions = [localStart.x, localStart.y, localStart.z, localEnd.x, localEnd.y, localEnd.z];

      const lineGeometry = new LineGeometry();
      lineGeometry.setPositions(positions);

      const lineMaterial = new LineMaterial({
        color: LINE_COLOR, // Brand green
        linewidth: 3,
        resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      });

      const line = new Line2(lineGeometry, lineMaterial);
      line.renderOrder = 998; // Below prompt direction lines (1000) and dashed box preview (1000)
      this.snappedBoxPreviewVisual.add(line);
    }

    this.scene.add(this.snappedBoxPreviewVisual);

    // Re-add box preview and prompt visuals so they render on top of the green
    // snapped lines (sortObjects is disabled, so scene child order determines
    // draw order with depthTest: false)
    if (this.boxPreviewVisual) {
      this.scene.remove(this.boxPreviewVisual);
      this.scene.add(this.boxPreviewVisual);
    }
  }

  /**
   * Clear the density-snapped box preview visualization
   */
  clearSnappedBoxPreview(): void {
    if (this.snappedBoxPreviewVisual) {
      this.scene.remove(this.snappedBoxPreviewVisual);
      this.snappedBoxPreviewVisual.traverse((obj) => {
        if (obj instanceof Line2) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.snappedBoxPreviewVisual = null;
    }
  }

  /**
   * Show an isolation plane perpendicular to a line direction at a given position.
   * Uses a heatmap gradient for smooth visualization with steep dropoff.
   */
  showIsolationPlane(position: THREE.Vector3, direction: THREE.Vector3, _floorHeight?: number): void {
    // Clear any existing isolation plane
    this.hideIsolationPlane();

    // Calculate the plane normal — in-plane perpendicular to the line
    // direction within the active base plane. On the floor plane this
    // reduces to `(-dir.y, dir.x, 0)` (the legacy XY perp). On a wall
    // plane this returns the wall-plane-tangent vector perpendicular to
    // the line (used to clip cloud points around the wall edge).
    const lineDir = direction.clone().normalize();
    const frame = this.coordinateTransformer.getBasePlaneFrame();
    let planeNormal: THREE.Vector3;
    if (frame) {
      planeNormal = new THREE.Vector3().crossVectors(frame.normal, lineDir);
      if (planeNormal.lengthSq() < 1e-9) planeNormal.set(-lineDir.y, lineDir.x, 0);
    } else {
      planeNormal = new THREE.Vector3(-lineDir.y, lineDir.x, 0);
    }
    planeNormal.normalize();

    // Calculate bounding box for plane size
    const boundingBox = this.viewer.getBoundingBox(this.viewer.scene.pointclouds);
    const size = boundingBox.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.y) / 2;

    // Position the plane at the given position
    const planePosition = position.clone();

    // Create the isolation plane with HEATMAP for a smooth gradient effect
    this.isolationPlane = new PointoramaPlane({
      position: planePosition,
      normal: planeNormal,
      clipTask: ClipTask.HEATMAP, // Use heatmap for gradient visualization
      tolerance: 0.005, // 0.5cm tolerance for steep gradient falloff
      radius: radius,
      selected: false,
      finished: false,
    });

    // Set gradient to PURE_PURPLE (black with white edge)
    this.isolationPlane.gradient = 'PURE_PURPLE';

    // Enable heatmap isolation to clip points outside tolerance
    this.isolationPlane.heatmapIsolate = true;

    this.viewer.scene.addPlane(this.isolationPlane);
  }

  /**
   * Hide and dispose of the isolation plane
   */
  hideIsolationPlane(): void {
    if (this.isolationPlane) {
      this.viewer.scene.removePlane(this.isolationPlane);
      this.isolationPlane = null;
    }
  }

  /**
   * Get the tracked prompt spheres map
   */
  getPromptSpheres(): Map<string, THREE.Mesh> {
    return this.promptSpheres;
  }

  /**
   * Render snap target spheres at the midpoints of detected lines.
   * These spheres appear when dragging a prompt to indicate potential snap targets.
   * @param lines The detected lines to render snap targets for
   * @param nearestLineId Optional ID of the nearest line to highlight
   */
  renderSnapTargets(lines: DetectedLine[], nearestLineId: string | null = null): void {
    // Clear existing snap targets
    this.clearSnapTargets();

    if (lines.length === 0) return;

    this.snapTargetVisuals = new THREE.Group();

    // Use the first line's start as local origin to avoid precision issues
    const localOrigin = lines[0].start.clone();
    this.snapTargetVisuals.position.copy(localOrigin);

    // Calculate sphere radius
    const sphereRadius = this.calculateDynamicSphereRadius();

    for (const line of lines) {
      // Skip hidden lines
      if (line.isHidden) continue;

      // Calculate midpoint of the line
      const midpoint = line.start.clone().add(line.end).multiplyScalar(0.5);
      const localMidpoint = midpoint.clone().sub(localOrigin);

      // Check if this is the nearest line (should be highlighted)
      const isNearest = line.id === nearestLineId;

      // Create sphere at midpoint
      const sphereGeometry = new THREE.SphereGeometry(isNearest ? sphereRadius * 1.5 : sphereRadius, 12, 12);
      const sphereMaterial = new THREE.MeshBasicMaterial({
        color: SNAP_TARGET_COLOR,
        depthTest: false,
        transparent: true,
        opacity: isNearest ? 1.0 : 0.7,
      });

      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      sphere.position.copy(localMidpoint);
      sphere.renderOrder = 1002; // Above prompt spheres
      sphere.userData.lineId = line.id;

      this.snapTargetVisuals.add(sphere);
    }

    this.scene.add(this.snapTargetVisuals);
  }

  /**
   * Clear the snap target spheres visualization
   */
  clearSnapTargets(): void {
    if (this.snapTargetVisuals) {
      this.scene.remove(this.snapTargetVisuals);
      this.snapTargetVisuals.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.snapTargetVisuals = null;
    }
  }

  // ========== Hover preview (3D wall hover) ==========

  private hoverPreviewGroup: THREE.Group | null = null;

  /**
   * Show a preview of the prompt that would be created at the given position/direction.
   * Renders with the "unconnected" style (red sphere + red direction lines).
   */
  renderHoverPreview(position: THREE.Vector3, direction: THREE.Vector3): void {
    this.clearHoverPreview();

    this.hoverPreviewGroup = new THREE.Group();
    this.hoverPreviewGroup.position.copy(position);

    const sphereRadius = this.calculateDynamicSphereRadius();
    const halfLength = 1.5; // Same default as direction line half-length

    // Red sphere at center
    const sphereGeometry = new THREE.SphereGeometry(sphereRadius, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: 0xf44336,
      depthTest: false,
      transparent: true,
      opacity: 0.6,
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.renderOrder = 1001;
    this.hoverPreviewGroup.add(sphere);

    // Direction lines in local space (center is origin)
    const posEnd = direction.clone().multiplyScalar(halfLength);
    const negEnd = direction.clone().multiplyScalar(-halfLength);
    this.addHoverDirectionLine(new THREE.Vector3(), posEnd);
    this.addHoverDirectionLine(new THREE.Vector3(), negEnd);

    this.scene.add(this.hoverPreviewGroup);
  }

  clearHoverPreview(): void {
    if (this.hoverPreviewGroup) {
      this.scene.remove(this.hoverPreviewGroup);
      this.hoverPreviewGroup.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof Line2) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.hoverPreviewGroup = null;
    }
  }

  private addHoverDirectionLine(start: THREE.Vector3, end: THREE.Vector3): void {
    if (!this.hoverPreviewGroup) return;

    const positions = [start.x, start.y, start.z, end.x, end.y, end.z];
    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions(positions);

    const lineMaterial = new LineMaterial({
      color: 0xf44336,
      linewidth: 3,
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      depthTest: false,
      transparent: true,
      opacity: 0.6,
    });

    const line = new Line2(lineGeometry, lineMaterial);
    line.renderOrder = 1000;
    this.hoverPreviewGroup.add(line);

    // Background line for visibility
    const bgMaterial = new LineMaterial({
      color: 0x000000,
      linewidth: 5,
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      depthTest: false,
      transparent: true,
      opacity: 0.2,
    });
    const bgLine = new Line2(lineGeometry.clone(), bgMaterial);
    bgLine.renderOrder = 999;
    this.hoverPreviewGroup.add(bgLine);
  }

  /**
   * Clean up all resources
   */
  dispose(): void {
    this.clearLinePreview();
    this.clearBoxPreview();
    this.clearPromptVisuals();
    this.hideIsolationPlane();
    this.clearSnapTargets();
    this.clearHoverPreview();
  }
}
